import { writable, derived } from "svelte/store";

const DEFAULT_PERMISSIONS = {
    role: null,
    channels: [],
    email: null,
    pages: {
        workspace_info: false,
        user_list: false,
        analytics: false,
        upgrades: false,
        trial_extension: false,
        promo_codes: false,
        promo_codes_view: false,
        admin_management: false
    }
};

function loadPermissions() {
    try {
        const stored = localStorage.getItem("admin_permissions");
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error("Failed to parse stored permissions:", e);
    }
    return DEFAULT_PERMISSIONS;
}

function createPermissionsStore() {
    const { subscribe, set, update } = writable(loadPermissions());

    return {
        subscribe,
        set: (value) => {
            localStorage.setItem("admin_permissions", JSON.stringify(value));
            set(value);
        },
        clear: () => {
            localStorage.removeItem("admin_permissions");
            set(DEFAULT_PERMISSIONS);
        },
        update
    };
}

export const permissions = createPermissionsStore();

// Derived store for checking specific page access
export const canAccess = derived(permissions, ($permissions) => {
    const upgrades = $permissions.pages?.upgrades ?? false;
    const trial_extension = $permissions.pages?.trial_extension ?? false;
    return {
        workspace_info: $permissions.pages?.workspace_info ?? false,
        user_list: $permissions.pages?.user_list ?? false,
        analytics: $permissions.pages?.analytics ?? false,
        upgrades,
        trial_extension,
        // Upgrades section is reachable by full upgraders OR trial-only extenders (marketing)
        upgrades_section: upgrades || trial_extension,
        promo_codes: $permissions.pages?.promo_codes ?? false,
        promo_codes_view: $permissions.pages?.promo_codes_view ?? false,
        // Promo section is reachable by writers OR read-only viewers (marketing)
        promo_section: ($permissions.pages?.promo_codes ?? false) || ($permissions.pages?.promo_codes_view ?? false),
        admin_management: $permissions.role === 'admin',
        // Intentionally admin-only UI gate (even if backend grants `billing_plans` to sales)
        billing_plans: $permissions.role === 'admin'
    };
});

// Map section names to permission keys
export const SECTION_PERMISSIONS = {
    dashboard: 'analytics',
    user_list: 'user_list',
    user_list_v2: 'user_list',
    workspaces: 'workspace_info',
    promo: 'promo_section',
    push: 'upgrades',
    upgrades: 'upgrades_section',
    admin_users: 'admin_management',
    billing_plans: 'billing_plans'
};

